import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useRegisterTopEscapeClose } from '../../hooks/useEscapeCloseStack';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  subscribeFundInvestorsByLender,
  createFundInvestor,
  createFundInvestorLegacyImport,
  updateFundInvestor,
  createFundInvestorInvitation,
  getFundInvestorFileDownloadUrl,
  setFundInvestorFullyExecutedContract,
  acknowledgeFundInvestorChequeSpecimen,
  requestQualificationDocs,
  reviewQualificationDocs,
  getQualificationDocsTemplate,
  isFundInvestorAttestationSatisfied,
} from '../../services/fundInvestorsService';
import {
  createFundInvestorCapitalTranche,
  subscribeFundInvestorCapitalTranches,
} from '../../services/fundInvestorCapitalTranchesService';
import { createFundInvestorRepayment, subscribeFundInvestorRepayments } from '../../services/fundInvestorRepaymentsService';
import { subscribeWithdrawalRequestsByLender, updateWithdrawalRequest } from '../../services/fundWithdrawalRequestsService';
import { subscribeCapitalAdditionProposalsByLender, respondToCapitalAdditionProposal } from '../../services/fundCapitalAdditionProposalsService';
import { recomputeFundInvestorPeriodAllocations } from '../../services/fundAllocationsAdminService';
import { subscribeLenderSettings } from '../../services/lenderSettingsService';
import { getFundPeriodSummaryForLenderOnce } from '../../services/fundPeriodSummariesService';
import { publishFundPeriodInvestorReport } from '../../services/fundPeriodReportService';
import {
  Users,
  Plus,
  Mail,
  ArrowDownToLine,
  PlusCircle,
  Banknote,
  LayoutGrid,
  ClipboardList,
  FileText,
  User,
  Upload,
  MessageCircle,
} from 'lucide-react';
import FundInvestorsImportModal from './FundInvestorsImportModal';
import DirectChatModal from '../directChat/DirectChatModal';
import { buildLenderFundInvestorConversationSpec } from '../../services/directChatService';

function formatAmount(n) {
  if (n == null || typeof n !== 'number' || Number.isNaN(n)) return '—';
  const s = String(Math.max(0, Math.floor(n)));
  const withSpaces = s.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${withSpaces} $`;
}

// Exact same helpers as for \"Amount received\" (Mark as received)
function formatPaidAmountDisplay(value) {
  if (value == null || value === '') return '';
  const num = typeof value === 'number'
    ? value
    : Number(String(value).replace(/\s/g, '').replace(',', '.').replace(/\$/g, ''));
  if (Number.isNaN(num) || num < 0) return '';
  const fixed = num.toFixed(2);
  const [intPart, decPart] = fixed.split('.');
  const withSpaces = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${withSpaces}.${decPart} $`;
}

function parsePaidAmountDisplay(str) {
  if (!str || typeof str !== 'string') return NaN;
  const cleaned = str.replace(/\s/g, '').replace(/\$/g, '').replace(',', '.').trim();
  return cleaned === '' ? NaN : Number(cleaned);
}

function formatDateWithLocale(str, locale) {
  if (!str || typeof str !== 'string') return '—';
  const d = str.slice(0, 10);
  if (!d) return '—';
  try {
    const dateLocale = locale === 'fr' ? 'fr-CA' : 'en-CA';
    return new Date(d + 'T12:00:00').toLocaleDateString(dateLocale, { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return d;
  }
}

/** Calendar month immediately before today (for default “process previous month”). */
function getPreviousCalendarMonthParts() {
  const d = new Date();
  const prev = new Date(d.getFullYear(), d.getMonth() - 1, 1);
  return { year: prev.getFullYear(), month: prev.getMonth() + 1 };
}

const STATUS_LABELS = {
  invited: { key: 'fundInvestors.statusInvited', color: 'bg-amber-100 text-amber-800' },
  invitation_accepted: { key: 'fundInvestors.statusInvitationAccepted', color: 'bg-sky-100 text-sky-800' },
  signed: { key: 'fundInvestors.statusSigned', color: 'bg-blue-100 text-blue-800' },
  funds_received: { key: 'fundInvestors.statusFundsReceived', color: 'bg-green-100 text-green-800' },
};

/**
 * Email set, no linked account yet — can queue platform signup invitation.
 * Covers invitation flow (invited / invitation_accepted) and file/legacy imports (funds_received + legacyCapitalImport).
 */
function needsFundInvestorPlatformInvite(item, lenderUid) {
  if (!item || item.lenderUid !== lenderUid) return false;
  if (!String(item.email || '').trim()) return false;
  if (item.investorUserId) return false;
  if (item.status === 'invited' || item.status === 'invitation_accepted') return true;
  if (item.legacyCapitalImport === true && item.status === 'funds_received') return true;
  return false;
}

/** Migration email (not the “new pool” template): legacy flag or historical placement awaiting account link. */
function useMigrationCapitalInvestorInviteEmail(item) {
  if (!item) return false;
  if (item.legacyCapitalImport === true) return true;
  if (item.status === 'funds_received' && !item.investorUserId) return true;
  return false;
}

function qualificationDocsInScope(item) {
  const s = item?.qualificationDocsRequestStatus || 'not_requested';
  return s === 'requested' || s === 'submitted' || s === 'reviewed';
}

/**
 * Ordered onboarding checks for linked investors (gestionnaire view).
 * @returns {{ stepIndex: number, totalSteps: number, labelKey: string } | null}
 */
function getCapitalInvestorOnboardingProgress(item) {
  if (item?.legacyCapitalImport) return null;
  if (!item?.investorUserId || item.status === 'funds_received') return null;

  const steps = [];

  steps.push({
    done: isFundInvestorAttestationSatisfied(item),
    labelKey: 'fundInvestors.onboarding.stepAttestation',
  });

  if (qualificationDocsInScope(item)) {
    const s = item.qualificationDocsRequestStatus || 'not_requested';
    const done = s === 'reviewed';
    const labelKey =
      s === 'submitted'
        ? 'fundInvestors.onboarding.stepQualDocsReview'
        : 'fundInvestors.onboarding.stepQualDocsInvestor';
    steps.push({ done, labelKey });
  }

  const hasContractTemplate = !!item.contractFileRef?.storagePath;
  if (hasContractTemplate) {
    steps.push({
      done: !!item.investorSignedContractFileRef?.storagePath,
      labelKey: 'fundInvestors.onboarding.stepContractInvestorSigned',
    });
    steps.push({
      done: !!item.fullyExecutedContractFileRef?.storagePath,
      labelKey: 'fundInvestors.onboarding.stepContractFullyExecuted',
    });
  }

  steps.push({
    done: !!item.chequeSpecimenFileRef?.storagePath,
    labelKey: 'fundInvestors.onboarding.stepCheque',
  });

  const firstOpen = steps.findIndex((s) => !s.done);
  if (firstOpen === -1) {
    if (item.investorUserId && item.status !== 'signed' && item.status !== 'funds_received') {
      return { kind: 'managerMarkSigned', labelKey: 'fundInvestors.onboarding.stepConfirmContractStatus' };
    }
    return null;
  }

  return {
    kind: 'progress',
    stepIndex: firstOpen + 1,
    totalSteps: steps.length,
    labelKey: steps[firstOpen].labelKey,
  };
}

export default function CapitalInvestorsPage() {
  const { currentUser, userProfile } = useAuth();
  const { t, language } = useLanguage();
  const [list, setList] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showLegacyImportModal, setShowLegacyImportModal] = useState(false);
  const [showFileImportModal, setShowFileImportModal] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState(false);
  const [inviteSuccessMessageKey, setInviteSuccessMessageKey] = useState('fundInvestors.invite.successMessage');
  const [lenderUiSettings, setLenderUiSettings] = useState(null);
  const [legacyImportSuccess, setLegacyImportSuccess] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [resendingId, setResendingId] = useState(null);
  const [resendSuccessId, setResendSuccessId] = useState(null);
  const [bulkInvitingAll, setBulkInvitingAll] = useState(false);
  const [bulkInviteAllResult, setBulkInviteAllResult] = useState(null);
  const [manageInvestorItem, setManageInvestorItem] = useState(null);
  const [withdrawalRequests, setWithdrawalRequests] = useState([]);
  const [capitalProposals, setCapitalProposals] = useState([]);
  const [updatingWithdrawalId, setUpdatingWithdrawalId] = useState(null);
  const [respondingProposalId, setRespondingProposalId] = useState(null);
  const [recomputingAllocations, setRecomputingAllocations] = useState(false);
  const [recomputeAllocationsResult, setRecomputeAllocationsResult] = useState(null);
  const [recomputeAllocationsError, setRecomputeAllocationsError] = useState(null);
  const defaultRecomputePeriod = useMemo(() => getPreviousCalendarMonthParts(), []);
  const [recomputeYear, setRecomputeYear] = useState(() => defaultRecomputePeriod.year);
  const [recomputeMonth, setRecomputeMonth] = useState(() => defaultRecomputePeriod.month);
  const recomputePeriodKey = useMemo(
    () => `${recomputeYear}-${String(recomputeMonth).padStart(2, '0')}`,
    [recomputeYear, recomputeMonth]
  );
  const [periodSummary, setPeriodSummary] = useState(null);
  const [periodSummaryLoading, setPeriodSummaryLoading] = useState(false);
  const [publishComment, setPublishComment] = useState('');
  const [publishingReport, setPublishingReport] = useState(false);
  const [publishReportError, setPublishReportError] = useState(null);
  const fundInvestorStatusSyncAttemptedRef = useRef(new Set());
  const [directChatModal, setDirectChatModal] = useState(null);

  const directChatUser = useMemo(() => {
    if (!currentUser) return null;
    return {
      ...currentUser,
      displayName: userProfile?.displayName || currentUser.displayName,
    };
  }, [currentUser, userProfile]);

  const refreshPeriodSummary = useCallback(async () => {
    if (!currentUser?.uid) return;
    setPeriodSummaryLoading(true);
    try {
      const s = await getFundPeriodSummaryForLenderOnce(currentUser.uid, recomputePeriodKey);
      setPeriodSummary(s);
      setPublishComment(s?.investorReportComment != null ? String(s.investorReportComment) : '');
    } catch {
      setPeriodSummary(null);
    } finally {
      setPeriodSummaryLoading(false);
    }
  }, [currentUser?.uid, recomputePeriodKey]);

  useEffect(() => {
    refreshPeriodSummary();
  }, [refreshPeriodSummary]);

  useEffect(() => {
    if (!currentUser?.uid) return;
    return subscribeFundInvestorsByLender(currentUser.uid, setList);
  }, [currentUser?.uid]);

  useEffect(() => {
    if (!currentUser?.uid) return;
    return subscribeLenderSettings(currentUser.uid, setLenderUiSettings);
  }, [currentUser?.uid]);

  useEffect(() => {
    if (!currentUser?.uid) return;
    return subscribeWithdrawalRequestsByLender(currentUser.uid, setWithdrawalRequests);
  }, [currentUser?.uid]);

  useEffect(() => {
    if (!currentUser?.uid) return;
    return subscribeCapitalAdditionProposalsByLender(currentUser.uid, setCapitalProposals);
  }, [currentUser?.uid]);

  /** Placements started before auto status: PDF final present but status still invitation — align with Firestore. */
  useEffect(() => {
    if (!currentUser?.uid || !list.length) return;
    for (const item of list) {
      if (item.lenderUid !== currentUser.uid) continue;
      if (!item.investorUserId) continue;
      if (item.status !== 'invited' && item.status !== 'invitation_accepted') continue;
      if (!item.fullyExecutedContractFileRef?.storagePath) continue;
      if (fundInvestorStatusSyncAttemptedRef.current.has(item.id)) continue;
      fundInvestorStatusSyncAttemptedRef.current.add(item.id);
      const patch = { status: 'signed' };
      if (!item.contractSignedAt) {
        patch.contractSignedAt = new Date();
      }
      updateFundInvestor(item.id, patch).catch((e) => {
        fundInvestorStatusSyncAttemptedRef.current.delete(item.id);
        console.error('fundInvestor status sync', item.id, e);
      });
    }
  }, [list, currentUser?.uid]);

  const formatDate = (str) => formatDateWithLocale(str, language);

  const formatFromFirestoreDate = (value) => {
    if (!value) return '—';
    const tsOrDate = value?.toDate?.() ? value.toDate() : value;
    if (tsOrDate instanceof Date) {
      const iso = tsOrDate.toISOString().slice(0, 10);
      return formatDate(iso);
    }
    return formatDate(String(tsOrDate));
  };

  const canManage = !!currentUser?.uid;

  const handleRecomputeAllocations = async () => {
    if (!currentUser?.uid) return;
    setRecomputingAllocations(true);
    setRecomputeAllocationsResult(null);
    setRecomputeAllocationsError(null);
    try {
      const data = await recomputeFundInvestorPeriodAllocations({
        year: recomputeYear,
        month: recomputeMonth,
      });
      setRecomputeAllocationsResult(data || { ok: true });
      await refreshPeriodSummary();
    } catch (e) {
      setRecomputeAllocationsError(e?.message || String(e));
    } finally {
      setRecomputingAllocations(false);
    }
  };

  const handlePublishReport = async (publish) => {
    if (!currentUser?.uid) return;
    setPublishingReport(true);
    setPublishReportError(null);
    try {
      if (publish) {
        await publishFundPeriodInvestorReport({
          periodKey: recomputePeriodKey,
          publish: true,
          investorReportComment: publishComment,
        });
      } else {
        await publishFundPeriodInvestorReport({
          periodKey: recomputePeriodKey,
          publish: false,
        });
      }
      await refreshPeriodSummary();
    } catch (e) {
      setPublishReportError(e?.message || String(e));
    } finally {
      setPublishingReport(false);
    }
  };

  const handleUpdateStatus = async (item, nextStatus) => {
    if (!currentUser?.uid || item.lenderUid !== currentUser.uid) return;
    setUpdatingId(item.id);
    try {
      const update = { status: nextStatus };
      const now = new Date();
      if (nextStatus === 'signed') {
        update.contractSignedAt = now;
      }
      if (nextStatus === 'funds_received') {
        update.fundsReceivedAt = now;
      }
      await updateFundInvestor(item.id, update);
    } catch (e) {
      console.error(e);
    } finally {
      setUpdatingId(null);
    }
  };

  const handleResendInvitation = async (item) => {
    if (!currentUser?.uid || item.lenderUid !== currentUser.uid || !item.email) return;
    setResendingId(item.id);
    setResendSuccessId(null);
    try {
      await createFundInvestorInvitation(
        currentUser.uid,
        item.id,
        item.email,
        item.displayName || null,
        useMigrationCapitalInvestorInviteEmail(item) ? { invitationScenario: 'migration' } : undefined
      );
      setResendSuccessId(item.id);
      setTimeout(() => setResendSuccessId(null), 3000);
    } catch (e) {
      console.error(e);
    } finally {
      setResendingId(null);
    }
  };

  const handleBulkInviteAllInvestors = async () => {
    if (!currentUser?.uid || bulkInvitingAll || pendingInvestorInvites.length === 0) return;
    if (!window.confirm(t('fundInvestors.bulkInviteAllConfirm'))) return;
    setBulkInvitingAll(true);
    setBulkInviteAllResult(null);
    let ok = 0;
    let failed = 0;
    try {
      for (const item of pendingInvestorInvites) {
        try {
          await createFundInvestorInvitation(
            currentUser.uid,
            item.id,
            item.email,
            item.displayName || null,
            useMigrationCapitalInvestorInviteEmail(item) ? { invitationScenario: 'migration' } : undefined
          );
          ok += 1;
        } catch (e) {
          console.error('bulk invite investor', item.id, e);
          failed += 1;
        }
      }
      setBulkInviteAllResult({ ok, failed });
      setTimeout(() => setBulkInviteAllResult(null), 10000);
    } finally {
      setBulkInvitingAll(false);
    }
  };

  const handleInviteSuccess = (opts) => {
    setShowInviteModal(false);
    setInviteSuccessMessageKey(
      opts?.skippedEmail ? 'fundInvestors.invite.skippedEmailFundConfig' : 'fundInvestors.invite.successMessage'
    );
    setInviteSuccess(true);
    setTimeout(() => setInviteSuccess(false), 6000);
  };

  const hasPendingWithdrawal = (item) => withdrawalRequests.some((r) => r.fundInvestorId === item.id && r.status === 'pending');
  const hasPendingProposal = (item) => capitalProposals.some((p) => p.fundInvestorId === item.id && p.status === 'pending');

  const sortedList = useMemo(() => {
    return [...list].sort((a, b) => {
      const aPending = withdrawalRequests.some((r) => r.fundInvestorId === a.id && r.status === 'pending') || capitalProposals.some((p) => p.fundInvestorId === a.id && p.status === 'pending');
      const bPending = withdrawalRequests.some((r) => r.fundInvestorId === b.id && r.status === 'pending') || capitalProposals.some((p) => p.fundInvestorId === b.id && p.status === 'pending');
      if (aPending && !bPending) return -1;
      if (!aPending && bPending) return 1;
      return (a.displayName || a.email || '').localeCompare(b.displayName || b.email || '', undefined, { sensitivity: 'base' });
    });
  }, [list, withdrawalRequests, capitalProposals]);

  /** Same rules as per-row « Renvoyer l’invitation » — includes legacy/file imports (fonds reçus) sans compte relié. */
  const pendingInvestorInvites = useMemo(() => {
    if (!currentUser?.uid) return [];
    return list.filter((item) => needsFundInvestorPlatformInvite(item, currentUser.uid));
  }, [list, currentUser?.uid]);

  const handleRespondProposal = async (proposalId, status) => {
    if (!currentUser?.uid) return;
    setRespondingProposalId(proposalId);
    try {
      await respondToCapitalAdditionProposal(proposalId, { status, respondedByUid: currentUser.uid });
    } catch (e) {
      console.error(e);
    } finally {
      setRespondingProposalId(null);
    }
  };

  const handleCompleteWithdrawalWithTransaction = async (requestId, fundInvestorId, payload) => {
    if (!currentUser?.uid || !fundInvestorId) return;
    const { transactionDate, amount, investingEntityType, investingCompanyId, investingDisplayName } = payload || {};
    const fundInvestor = list.find((x) => x.id === fundInvestorId);
    if (!fundInvestor?.lenderUid) return;
    setUpdatingWithdrawalId(requestId);
    try {
      await createFundInvestorRepayment(fundInvestorId, {
        lenderUid: fundInvestor.lenderUid,
        investorUserId: fundInvestor.investorUserId || null,
        amount: Number(amount),
        repaidAt: transactionDate,
        repaymentType: 'partial',
        investingEntityType: investingEntityType || 'individual',
        investingCompanyId: investingCompanyId || null,
        investingDisplayName: investingDisplayName || null,
      });
      await updateWithdrawalRequest(requestId, { status: 'completed', completedByUid: currentUser.uid, repaidAt: transactionDate });
    } catch (e) {
      console.error(e);
    } finally {
      setUpdatingWithdrawalId(null);
    }
  };

  const handleConfirmCapitalReceipt = async (proposalId, { transactionDate, amount }) => {
    if (!currentUser?.uid) return;
    const proposal = capitalProposals.find((p) => p.id === proposalId);
    if (!proposal?.fundInvestorId) return;
    const fundInvestor = list.find((x) => x.id === proposal.fundInvestorId);
    if (!fundInvestor) return;
    setRespondingProposalId(proposalId);
    try {
      const { confirmCapitalAdditionReceipt } = await import('../../services/fundCapitalAdditionProposalsService');
      await confirmCapitalAdditionReceipt(proposalId, { fundsReceivedAt: transactionDate, fundsReceivedAmount: Number(amount) });
      const currentAmount = Number(fundInvestor.investedAmount) || 0;
      await updateFundInvestor(proposal.fundInvestorId, { investedAmount: currentAmount + Number(amount) });
      const dateStr = String(transactionDate || '').trim().slice(0, 10);
      await createFundInvestorCapitalTranche({
        fundInvestorId: proposal.fundInvestorId,
        lenderUid: currentUser.uid,
        amount: Number(amount),
        startDate: dateStr,
        type: 'capital_addition',
        sourceProposalId: proposalId,
      });
    } catch (e) {
      console.error(e);
    } finally {
      setRespondingProposalId(null);
    }
  };

  return (
    <div>
      <div className="mb-4 rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="px-4 py-4 flex flex-col gap-4">
          <div className="flex items-center gap-2 min-w-0">
            <Users className="w-6 h-6 text-indigo-600 shrink-0" aria-hidden />
            <h2 className="text-xl font-semibold text-gray-800 truncate">{t('home.sectionCapitalInvestors')}</h2>
          </div>
          <div className="flex flex-col lg:flex-row lg:items-stretch gap-3 pt-3 border-t border-gray-100">
            <div className="w-full lg:w-1/2 min-w-0 rounded-lg border border-gray-200 bg-gray-50/90 px-3 py-2">
              <div className="text-xs font-medium text-gray-600 mb-1.5">{t('capitalInvestors.recomputePeriodLabel')}</div>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <select
                  className="text-sm border border-gray-300 rounded-md px-2 py-1.5 bg-white min-w-[7.5rem]"
                  value={recomputeMonth}
                  onChange={(e) => setRecomputeMonth(Number(e.target.value))}
                >
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                    <option key={m} value={m}>
                      {new Date(2000, m - 1, 1).toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-CA', {
                        month: 'long',
                      })}
                    </option>
                  ))}
                </select>
                <select
                  className="text-sm border border-gray-300 rounded-md px-2 py-1.5 bg-white min-w-[5rem]"
                  value={recomputeYear}
                  onChange={(e) => setRecomputeYear(Number(e.target.value))}
                >
                  {Array.from({ length: 7 }, (_, i) => new Date().getFullYear() - 3 + i).map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                onClick={handleRecomputeAllocations}
                disabled={!canManage || recomputingAllocations}
                className="inline-flex items-center gap-2 px-3 py-2 border border-indigo-200 text-indigo-700 rounded-lg hover:bg-indigo-50 disabled:opacity-50 text-sm"
                title={t('capitalInvestors.recomputePeriodHelp')}
              >
                {recomputingAllocations ? t('capitalInvestors.recomputeProgress') : t('capitalInvestors.recomputeButton')}
              </button>
            </div>
            <div className="w-full lg:w-1/2 min-w-0 flex flex-col gap-2">
              <button
                type="button"
                onClick={() => setShowInviteModal(true)}
                disabled={!canManage}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 w-full bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium disabled:opacity-50"
              >
                <Plus className="w-4 h-4 shrink-0" />
                {t('fundInvestors.invite.button')}
              </button>
              <button
                type="button"
                onClick={() => setShowFileImportModal(true)}
                disabled={!canManage}
                className="inline-flex items-center justify-center gap-2 px-3 py-2 w-full border border-slate-300 text-slate-800 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors text-sm disabled:opacity-50"
              >
                <Upload className="w-4 h-4 shrink-0" />
                {t('fundInvestors.fileImport.button')}
              </button>
              <button
                type="button"
                onClick={() => setShowLegacyImportModal(true)}
                disabled={!canManage}
                className="inline-flex items-center justify-center gap-2 px-3 py-2 w-full border border-amber-300 text-amber-900 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors text-sm disabled:opacity-50"
              >
                <ClipboardList className="w-4 h-4 shrink-0" />
                {t('fundInvestors.legacyImport.button')}
              </button>
              {canManage && pendingInvestorInvites.length > 0 && (
                <button
                  type="button"
                  onClick={handleBulkInviteAllInvestors}
                  disabled={bulkInvitingAll}
                  className="inline-flex items-center justify-center gap-2 px-3 py-2 w-full border border-indigo-400 text-indigo-900 bg-indigo-100/90 rounded-lg hover:bg-indigo-200/80 transition-colors text-sm font-medium disabled:opacity-50"
                  title={t('fundInvestors.bulkInviteAllSummary').replace('{n}', String(pendingInvestorInvites.length))}
                >
                  {bulkInvitingAll ? (
                    t('fundInvestors.bulkInviteAllSending')
                  ) : (
                    <>
                      <Mail className="w-4 h-4 shrink-0" />
                      {t('fundInvestors.bulkInviteAllButton')}
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {bulkInviteAllResult && (
        <div
          className={`mb-4 p-3 rounded-lg border text-sm ${
            bulkInviteAllResult.failed > 0
              ? 'bg-amber-50 border-amber-200 text-amber-900'
              : 'bg-green-50 border-green-200 text-green-800'
          }`}
        >
          {bulkInviteAllResult.failed > 0
            ? t('fundInvestors.bulkInviteAllResultPartial')
                .replace('{ok}', String(bulkInviteAllResult.ok))
                .replace('{failed}', String(bulkInviteAllResult.failed))
            : t('fundInvestors.bulkInviteAllResultOk').replace('{n}', String(bulkInviteAllResult.ok))}
        </div>
      )}

      {recomputeAllocationsError && (
        <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
          {recomputeAllocationsError}
        </div>
      )}
      {recomputeAllocationsResult && (
        <div className="mb-3 p-3 bg-sky-50 border border-sky-200 rounded-lg text-sky-900 text-sm">
          <div className="font-medium">Allocations mises à jour</div>
          <div className="text-xs mt-0.5">
            {recomputeAllocationsResult.periodKey ? `Période: ${recomputeAllocationsResult.periodKey} — ` : ''}
            {recomputeAllocationsResult.totalInterestReceived != null ? `Intérêts reçus: ${formatPaidAmountDisplay(recomputeAllocationsResult.totalInterestReceived)} — ` : ''}
            {recomputeAllocationsResult.totalCapitalFund != null ? `Base allocation (Σ capital moy./jour): ${formatPaidAmountDisplay(recomputeAllocationsResult.totalCapitalFund)}` : ''}
          </div>
        </div>
      )}

      <div className="mb-4 p-4 border border-gray-200 rounded-xl bg-white shadow-sm space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">{t('capitalInvestors.publishReportTitle')}</h3>
            <p className="text-xs text-gray-600 mt-0.5 max-w-2xl">{t('capitalInvestors.publishReportHelp')}</p>
          </div>
          <span
            className={`text-xs font-medium px-2 py-0.5 rounded ${
              periodSummary?.investorReportPublishedAt
                ? 'bg-green-100 text-green-800'
                : 'bg-gray-100 text-gray-700'
            }`}
          >
            {periodSummaryLoading
              ? '…'
              : periodSummary?.investorReportPublishedAt
                ? t('capitalInvestors.reportPublished')
                : t('capitalInvestors.reportDraft')}
          </span>
        </div>
        {!periodSummary && !periodSummaryLoading && (
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-2 py-1.5">
            {t('capitalInvestors.summaryMissing')}
          </p>
        )}
        {publishReportError && (
          <p className="text-xs text-red-700 bg-red-50 border border-red-100 rounded-lg px-2 py-1.5">{publishReportError}</p>
        )}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">{t('capitalInvestors.managerCommentLabel')}</label>
          <textarea
            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 min-h-[72px]"
            value={publishComment}
            onChange={(e) => setPublishComment(e.target.value)}
            disabled={publishingReport || !periodSummary}
            placeholder=""
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={publishingReport || !periodSummary}
            onClick={() => handlePublishReport(true)}
            className="inline-flex items-center px-3 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
          >
            {publishingReport ? '…' : t('capitalInvestors.publishButton')}
          </button>
          <button
            type="button"
            disabled={publishingReport || !periodSummary?.investorReportPublishedAt}
            onClick={() => handlePublishReport(false)}
            className="inline-flex items-center px-3 py-2 rounded-lg border border-gray-300 text-gray-800 text-sm hover:bg-gray-50 disabled:opacity-50"
          >
            {t('capitalInvestors.unpublishButton')}
          </button>
        </div>
      </div>

      {lenderUiSettings?.fundInConfiguration && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-900 text-sm">
          {t('fundInvestors.fundInConfigurationBanner')}
        </div>
      )}
      {inviteSuccess && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">
          {t(inviteSuccessMessageKey)}
        </div>
      )}
      {legacyImportSuccess && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">
          {t('fundInvestors.legacyImport.successMessage')}
        </div>
      )}

      {list.length === 0 ? (
        <p className="text-gray-500 py-4">{t('fundInvestors.empty')}</p>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('fundInvestors.table.email')}</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('fundInvestors.table.amount')}</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('fundInvestors.table.startDate')}</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('fundInvestors.table.status')}</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('fundInvestors.table.actions')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedList.map((item) => {
                const isInvitedButLinked = item.status === 'invited' && item.investorUserId;
                const statusInfo = isInvitedButLinked
                  ? STATUS_LABELS.invitation_accepted
                  : (STATUS_LABELS[item.status] || STATUS_LABELS.invited);
                const showWithdrawalBadge = hasPendingWithdrawal(item);
                const showProposalBadge = hasPendingProposal(item);
                const onboarding =
                  item.investorUserId && item.status !== 'funds_received'
                    ? getCapitalInvestorOnboardingProgress(item)
                    : null;
                return (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">
                      <span className="font-medium">{item.displayName || item.email}</span>
                      {item.displayName && <span className="block text-gray-500 text-xs">{item.email}</span>}
                      {(item.investingEntityType === 'company' && item.investingDisplayName) && (
                        <span className="block text-gray-500 text-xs">{t('fundInvestors.investingHolder')}: {item.investingDisplayName}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{formatAmount(item.investedAmount)}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{formatDate(item.interestStartDate)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${statusInfo.color}`}>
                        {t(statusInfo.key)}
                      </span>
                      {showWithdrawalBadge && (
                        <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 ml-1">
                          {t('fundInvestors.statusWithdrawalRequest')}
                        </span>
                      )}
                      {showProposalBadge && (
                        <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-sky-100 text-sky-800 ml-1">
                          {t('fundInvestors.statusCapitalAdditionRequest')}
                        </span>
                      )}
                      {item.legacyCapitalImport && (
                        <span
                          className="block mt-1 text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded px-1.5 py-0.5 w-fit max-w-xs"
                          title={t('fundInvestors.legacyImport.badgeHint')}
                        >
                          {t('fundInvestors.legacyImport.badge')}
                        </span>
                      )}
                      {item.chequeSpecimenPendingLenderNotice && (
                        <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-teal-100 text-teal-900 ml-1">
                          {t('fundInvestors.chequeSpecimen.pendingBadge')}
                        </span>
                      )}
                      {item.investorSignedContractPendingLenderNotice && (
                        <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-violet-100 text-violet-900 ml-1">
                          {t('fundInvestors.contractSigned.pendingInvestorSignedBadge')}
                        </span>
                      )}
                      {item.investorUserId && !isFundInvestorAttestationSatisfied(item) && (
                        <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-900 ml-1">
                          {t('fundInvestors.qualified.lenderPendingBadge')}
                        </span>
                      )}
                      {!item.legacyCapitalImport && item.contractSignedAt && (
                        <span className="block text-xs text-gray-500 mt-0.5">
                          {t('fundInvestors.signedAt')}: {formatFromFirestoreDate(item.contractSignedAt)}
                        </span>
                      )}
                      {!item.legacyCapitalImport && item.fundsReceivedAt && (
                        <span className="block text-xs text-gray-500">
                          {t('fundInvestors.fundsReceivedAt')}: {formatFromFirestoreDate(item.fundsReceivedAt)}
                        </span>
                      )}
                      {item.legacyCapitalImport && (
                        <span className="block text-xs text-gray-500 mt-0.5 max-w-[18rem] leading-snug">
                          {t('fundInvestors.legacyImport.listStatusNote')}
                        </span>
                      )}
                      {onboarding && (
                        <span className="block text-xs text-slate-600 mt-1 max-w-[14rem] leading-snug">
                          {onboarding.kind === 'managerMarkSigned'
                            ? t(onboarding.labelKey)
                            : language === 'fr'
                              ? `Mise en route — étape ${onboarding.stepIndex} sur ${onboarding.totalSteps} : ${t(onboarding.labelKey)}`
                              : `Onboarding — Step ${onboarding.stepIndex} of ${onboarding.totalSteps}: ${t(onboarding.labelKey)}`}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-xs text-gray-700">
                      {canManage && item.lenderUid === currentUser.uid && (
                        <div className="flex flex-wrap items-center justify-end gap-1">
                          {needsFundInvestorPlatformInvite(item, currentUser.uid) && (
                            <button
                              type="button"
                              onClick={() => handleResendInvitation(item)}
                              disabled={resendingId === item.id}
                              className="inline-flex items-center gap-1 px-2 py-1 border border-amber-500 text-amber-700 rounded hover:bg-amber-50 disabled:opacity-50"
                              title={t('fundInvestors.resendInvitation')}
                            >
                              <Mail className="w-3.5 h-3.5" />
                              {resendingId === item.id ? t('fundInvestors.resendSending') : t('fundInvestors.resendInvitation')}
                            </button>
                          )}
                          {(item.status === 'invited' || item.status === 'invitation_accepted') && (
                            <>
                              {(() => {
                                const hasContractTemplate = !!item.contractFileRef?.storagePath;
                                const hasInvestorSignedCopy = !!item.investorSignedContractFileRef?.storagePath;
                                const hasFullyExecuted = !!item.fullyExecutedContractFileRef?.storagePath;
                                const stuckWithFinalPdf =
                                  hasFullyExecuted &&
                                  (item.status === 'invited' || item.status === 'invitation_accepted');
                                const contractGateOk =
                                  !hasContractTemplate || hasInvestorSignedCopy || hasFullyExecuted;
                                const canMarkSigned =
                                  !!item.investorUserId &&
                                  isFundInvestorAttestationSatisfied(item) &&
                                  contractGateOk &&
                                  (!hasFullyExecuted || stuckWithFinalPdf);
                                const markSignedTitle = !item.investorUserId
                                  ? t('fundInvestors.qualified.lenderBlockMarkSignedNeedLink')
                                  : !isFundInvestorAttestationSatisfied(item)
                                    ? t('fundInvestors.qualified.lenderBlockMarkSignedNeedAttestation')
                                    : hasContractTemplate && !hasInvestorSignedCopy && !hasFullyExecuted
                                      ? t('fundInvestors.contractSigned.lenderBlockMarkSignedNeedInvestorSigned')
                                      : t('fundInvestors.actions.markSignedExplainer');
                                return (
                                  <button
                                    type="button"
                                    onClick={() => handleUpdateStatus(item, 'signed')}
                                    disabled={updatingId === item.id || !canMarkSigned}
                                    title={
                                      !canMarkSigned
                                        ? markSignedTitle
                                        : stuckWithFinalPdf
                                          ? t('fundInvestors.actions.markSignedSyncStuck')
                                          : t('fundInvestors.actions.markSignedExplainer')
                                    }
                                    className="inline-flex items-center px-2 py-1 border border-blue-500 text-blue-600 rounded hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {t('fundInvestors.actions.markSigned')}
                                  </button>
                                );
                              })()}
                            </>
                          )}
                          {item.status === 'signed' && (
                            <button
                              type="button"
                              onClick={() => handleUpdateStatus(item, 'funds_received')}
                              disabled={updatingId === item.id}
                              className="inline-flex items-center px-2 py-1 border border-green-500 text-green-600 rounded hover:bg-green-50 disabled:opacity-50"
                            >
                              {t('fundInvestors.actions.markFundsReceived')}
                            </button>
                          )}
                          {resendSuccessId === item.id && (
                            <span className="text-green-600 text-xs">{t('fundInvestors.resendSuccess')}</span>
                          )}
                          <button
                            type="button"
                            onClick={() => setManageInvestorItem(item)}
                            className="inline-flex items-center gap-1 px-2 py-1 border border-indigo-500 text-indigo-600 rounded hover:bg-indigo-50"
                            title={t('fundInvestors.manageCapitalInvestor')}
                          >
                            <Users className="w-3.5 h-3.5" />
                            {t('fundInvestors.manageCapitalInvestor')}
                          </button>
                          {item.investorUserId && item.lenderUid && (
                            <button
                              type="button"
                              onClick={() => {
                                const spec = buildLenderFundInvestorConversationSpec(
                                  item.id,
                                  item.lenderUid,
                                  item.investorUserId,
                                );
                                setDirectChatModal({
                                  conversationId: spec.conversationId,
                                  ensureSpec: {
                                    participantUids: spec.participantUids,
                                    kind: spec.kind,
                                    context: spec.context,
                                  },
                                  title: t('fundInvestors.messagePrivate'),
                                  subtitle: item.displayName || item.email || spec.conversationId,
                                });
                              }}
                              className="inline-flex items-center gap-1 px-2 py-1 border border-slate-400 text-slate-700 rounded hover:bg-slate-50"
                              title={t('fundInvestors.messagePrivate')}
                            >
                              <MessageCircle className="w-3.5 h-3.5" />
                              {t('fundInvestors.messagePrivate')}
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showInviteModal && (
        <InviteFundInvestorModalInner
          lenderUid={currentUser?.uid}
          fundInConfiguration={lenderUiSettings?.fundInConfiguration === true}
          onClose={() => setShowInviteModal(false)}
          onSuccess={handleInviteSuccess}
          t={t}
        />
      )}

      {showLegacyImportModal && (
        <LegacyImportFundInvestorModalInner
          lenderUid={currentUser?.uid}
          fundInConfiguration={lenderUiSettings?.fundInConfiguration === true}
          onClose={() => setShowLegacyImportModal(false)}
          onSuccess={() => {
            setLegacyImportSuccess(true);
            setShowLegacyImportModal(false);
            setTimeout(() => setLegacyImportSuccess(false), 8000);
          }}
          t={t}
        />
      )}

      {showFileImportModal && (
        <FundInvestorsImportModal
          open={showFileImportModal}
          onClose={() => setShowFileImportModal(false)}
          lenderUid={currentUser?.uid}
          fundInConfiguration={lenderUiSettings?.fundInConfiguration === true}
          existingInvestors={list}
          t={t}
          language={language}
        />
      )}

      {manageInvestorItem && (
        <ManageCapitalInvestorModal
          item={list.find((i) => i.id === manageInvestorItem.id) ?? manageInvestorItem}
          onClose={() => setManageInvestorItem(null)}
          withdrawalRequests={withdrawalRequests.filter((r) => r.fundInvestorId === manageInvestorItem.id)}
          capitalProposals={capitalProposals.filter((p) => p.fundInvestorId === manageInvestorItem.id)}
          formatAmount={formatAmount}
          formatDate={formatDate}
          formatFromFirestoreDate={formatFromFirestoreDate}
          t={t}
          language={language}
          onCompleteWithdrawalWithTransaction={handleCompleteWithdrawalWithTransaction}
          onConfirmCapitalReceipt={handleConfirmCapitalReceipt}
          onRespondProposal={handleRespondProposal}
          updatingWithdrawalId={updatingWithdrawalId}
          respondingProposalId={respondingProposalId}
        />
      )}
      {directChatModal && directChatUser && (
        <DirectChatModal
          onClose={() => setDirectChatModal(null)}
          conversationId={directChatModal.conversationId}
          ensureSpec={directChatModal.ensureSpec}
          title={directChatModal.title}
          subtitle={directChatModal.subtitle}
          currentUser={directChatUser}
        />
      )}
    </div>
  );
}

function InviteFundInvestorModalInner({ lenderUid, fundInConfiguration = false, onClose, onSuccess, t }) {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [investedAmount, setInvestedAmount] = useState('');
  const [interestStartDate, setInterestStartDate] = useState('');
  const [contractFile, setContractFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const trimmedEmail = (email || '').trim().toLowerCase();
    if (!trimmedEmail) {
      setError(t('fundInvestors.invite.errorEmail'));
      return;
    }
    const amount = investedAmount === '' ? 0 : parsePaidAmountDisplay(String(investedAmount));
    if (Number.isNaN(amount) || amount < 0) {
      setError(t('fundInvestors.invite.errorAmount'));
      return;
    }
    if (!lenderUid) {
      setError(t('fundInvestors.invite.errorGeneric'));
      return;
    }
    setSubmitting(true);
    try {
      const fundInvestorId = await createFundInvestor(
        {
          lenderUid,
          email: trimmedEmail,
          displayName: displayName.trim() || null,
          investedAmount: amount,
          interestStartDate: interestStartDate || null,
        },
        contractFile || undefined
      );
      if (fundInvestorId && !fundInConfiguration) {
        await createFundInvestorInvitation(
          lenderUid,
          fundInvestorId,
          trimmedEmail,
          displayName.trim() || null
        );
      }
      onSuccess({ skippedEmail: fundInConfiguration });
    } catch (err) {
      setError(err.message || t('fundInvestors.invite.errorGeneric'));
    } finally {
      setSubmitting(false);
    }
  };

  useRegisterTopEscapeClose(() => {
    if (!submitting) onClose();
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800">{t('fundInvestors.invite.title')}</h3>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-gray-100 text-gray-600">
            <span className="sr-only">Fermer</span>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && <p className="text-sm text-red-600 bg-red-50 p-2 rounded">{error}</p>}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('fundInvestors.invite.email')} *</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              placeholder="investisseur@exemple.com"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('fundInvestors.invite.displayName')}</label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              placeholder={t('fundInvestors.invite.displayNamePlaceholder')}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('fundInvestors.invite.investedAmount')}</label>
            <input
              type="text"
              value={investedAmount}
              onChange={(e) => setInvestedAmount(e.target.value)}
              onBlur={(e) => {
                const parsed = parsePaidAmountDisplay(e.target.value);
                if (!Number.isNaN(parsed) && parsed >= 0) {
                  setInvestedAmount(formatPaidAmountDisplay(parsed));
                }
              }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              placeholder="0"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('fundInvestors.invite.interestStartDate')}</label>
            <input
              type="date"
              value={interestStartDate}
              onChange={(e) => setInterestStartDate(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('fundInvestors.invite.contractPdf')}</label>
            <input
              type="file"
              accept=".pdf,application/pdf"
              onChange={(e) => setContractFile(e.target.files?.[0] || null)}
              className="w-full text-sm text-gray-600 file:mr-2 file:py-2 file:px-3 file:rounded file:border file:border-gray-300 file:bg-gray-100"
            />
          </div>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
              {t('cancel')}
            </button>
            <button type="submit" disabled={submitting} className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
              {submitting ? t('fundInvestors.invite.sending') : t('fundInvestors.invite.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function LegacyImportFundInvestorModalInner({ lenderUid, fundInConfiguration = false, onClose, onSuccess, t }) {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [sendInvitation, setSendInvitation] = useState(false);

  useEffect(() => {
    if (fundInConfiguration) setSendInvitation(false);
  }, [fundInConfiguration]);
  const [trancheRows, setTrancheRows] = useState([{ amount: '', startDate: '', note: '' }]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const updateRow = (index, field, value) => {
    setTrancheRows((rows) => rows.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const trimmedEmail = (email || '').trim().toLowerCase();
    if (!trimmedEmail) {
      setError(t('fundInvestors.invite.errorEmail'));
      return;
    }
    if (!lenderUid) {
      setError(t('fundInvestors.invite.errorGeneric'));
      return;
    }
    const tranches = [];
    for (const row of trancheRows) {
      const amt = row.amount === '' ? NaN : parsePaidAmountDisplay(String(row.amount));
      const sd = (row.startDate || '').trim().slice(0, 10);
      if (row.amount === '' && !sd && !(row.note || '').trim()) continue;
      if (Number.isNaN(amt) || amt <= 0 || !sd) {
        setError(t('fundInvestors.legacyImport.errorTranche'));
        return;
      }
      tranches.push({
        amount: amt,
        startDate: sd,
        note: (row.note || '').trim() || null,
      });
    }
    if (tranches.length === 0) {
      setError(t('fundInvestors.legacyImport.errorNeedTranche'));
      return;
    }
    setSubmitting(true);
    try {
      await createFundInvestorLegacyImport({
        lenderUid,
        email: trimmedEmail,
        displayName: displayName.trim() || null,
        tranches,
        sendInvitation: fundInConfiguration ? false : sendInvitation,
      });
      onSuccess();
    } catch (err) {
      setError(err.message || t('fundInvestors.invite.errorGeneric'));
    } finally {
      setSubmitting(false);
    }
  };

  useRegisterTopEscapeClose(() => {
    if (!submitting) onClose();
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800">{t('fundInvestors.legacyImport.title')}</h3>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-gray-100 text-gray-600">
            <span className="sr-only">Close</span>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <p className="text-sm text-gray-600 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
            {t('fundInvestors.legacyImport.disclaimer')}
          </p>
          {error && <p className="text-sm text-red-600 bg-red-50 p-2 rounded">{error}</p>}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('fundInvestors.invite.email')} *</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('fundInvestors.invite.displayName')}</label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-800">{t('fundInvestors.legacyImport.tranchesTitle')}</span>
              <button
                type="button"
                onClick={() => setTrancheRows((r) => [...r, { amount: '', startDate: '', note: '' }])}
                className="text-sm text-indigo-600 hover:text-indigo-800"
              >
                + {t('fundInvestors.legacyImport.addTranche')}
              </button>
            </div>
            {trancheRows.map((row, index) => (
              <div key={index} className="grid gap-2 sm:grid-cols-12 border border-gray-100 rounded-lg p-3 bg-gray-50/80">
                <div className="sm:col-span-4">
                  <label className="block text-xs text-gray-600 mb-0.5">{t('fundInvestors.legacyImport.trancheAmount')}</label>
                  <input
                    type="text"
                    value={row.amount}
                    onChange={(e) => updateRow(index, 'amount', e.target.value)}
                    onBlur={(e) => {
                      const parsed = parsePaidAmountDisplay(e.target.value);
                      if (!Number.isNaN(parsed) && parsed >= 0) {
                        updateRow(index, 'amount', formatPaidAmountDisplay(parsed));
                      }
                    }}
                    className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                    placeholder="0.00"
                  />
                </div>
                <div className="sm:col-span-4">
                  <label className="block text-xs text-gray-600 mb-0.5">{t('fundInvestors.invite.interestStartDate')}</label>
                  <input
                    type="date"
                    value={row.startDate}
                    onChange={(e) => updateRow(index, 'startDate', e.target.value)}
                    className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                  />
                </div>
                <div className="sm:col-span-3">
                  <label className="block text-xs text-gray-600 mb-0.5">{t('fundInvestors.legacyImport.trancheNote')}</label>
                  <input
                    type="text"
                    value={row.note}
                    onChange={(e) => updateRow(index, 'note', e.target.value)}
                    className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                  />
                </div>
                <div className="sm:col-span-1 flex items-end">
                  {trancheRows.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setTrancheRows((r) => r.filter((_, j) => j !== index))}
                      className="text-sm text-red-600 hover:text-red-800 px-1"
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <label className={`flex items-center gap-2 text-sm ${fundInConfiguration ? 'text-gray-400' : 'text-gray-700'}`}>
            <input
              type="checkbox"
              checked={sendInvitation}
              disabled={fundInConfiguration}
              onChange={(e) => setSendInvitation(e.target.checked)}
            />
            {t('fundInvestors.legacyImport.sendInvitation')}
            {fundInConfiguration && (
              <span className="text-xs text-gray-500">({t('fundInvestors.legacyImport.sendInvitationDisabledFundConfig')})</span>
            )}
          </label>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
              {t('cancel')}
            </button>
            <button type="submit" disabled={submitting} className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50">
              {submitting ? t('fundInvestors.invite.sending') : t('fundInvestors.legacyImport.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ManageCapitalInvestorModal({
  item,
  onClose,
  withdrawalRequests,
  capitalProposals,
  formatAmount,
  formatDate,
  formatFromFirestoreDate,
  t,
  language,
  onCompleteWithdrawalWithTransaction,
  onConfirmCapitalReceipt,
  onRespondProposal,
  updatingWithdrawalId,
  respondingProposalId,
}) {
  const [repayments, setRepayments] = useState([]);
  const [transactionForm, setTransactionForm] = useState(null);
  const [transactionDate, setTransactionDate] = useState('');
  const [transactionAmount, setTransactionAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [chequeAckSubmitting, setChequeAckSubmitting] = useState(false);
  const [chequeBannerError, setChequeBannerError] = useState('');
  const [qualificationDocsRequestLoading, setQualificationDocsRequestLoading] = useState(false);
  const [qualificationDocsActionError, setQualificationDocsActionError] = useState('');
  const [lenderModalTab, setLenderModalTab] = useState('overview');
  const [fullyExecutedUploading, setFullyExecutedUploading] = useState(false);
  const [fullyExecutedError, setFullyExecutedError] = useState('');
  const [fullyExecutedSuccess, setFullyExecutedSuccess] = useState('');

  const today = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    setLenderModalTab('overview');
  }, [item?.id]);

  useEffect(() => {
    setFullyExecutedError('');
    setFullyExecutedSuccess('');
  }, [item?.id]);

  useEffect(() => {
    if (transactionForm) setLenderModalTab('requests');
  }, [transactionForm]);

  useEffect(() => {
    if (!item?.id || !item?.lenderUid) return;
    return subscribeFundInvestorRepayments(item.id, item.lenderUid, setRepayments);
  }, [item?.id, item?.lenderUid]);

  const [capitalTranches, setCapitalTranches] = useState([]);

  useEffect(() => {
    if (!item?.id || !item?.lenderUid) {
      setCapitalTranches([]);
      return () => {};
    }
    return subscribeFundInvestorCapitalTranches(item.id, item.lenderUid, setCapitalTranches);
  }, [item?.id, item?.lenderUid]);

  const positiveInflowTranches = useMemo(
    () =>
      (capitalTranches || []).filter(
        (t) =>
          Number(t.amount) > 0 &&
          ['initial', 'manual', 'capital_addition'].includes(String(t.type || ''))
      ),
    [capitalTranches]
  );

  const hasTrancheBreakdown = positiveInflowTranches.length > 0;

  useEffect(() => {
    if (!transactionForm) return;
    const todayStr = new Date().toISOString().slice(0, 10);
    setTransactionDate(todayStr);
    if (transactionForm.type === 'withdrawal') {
      const req = transactionForm.request;
      setTransactionAmount(req.amountRequested != null ? formatPaidAmountDisplay(req.amountRequested) : '');
    } else {
      const p = transactionForm.proposal;
      setTransactionAmount(p.proposedAmount != null ? formatPaidAmountDisplay(p.proposedAmount) : '');
    }
  }, [transactionForm]);

  const invested = item?.investedAmount ?? 0;
  const capitalAddedFromProposals = capitalProposals
    .filter((p) => p.fundsReceivedAt || p.fundsReceivedAmount != null)
    .reduce((sum, p) => sum + (Number(p.fundsReceivedAmount ?? p.proposedAmount) || 0), 0);

  const initialInvested = hasTrancheBreakdown
    ? positiveInflowTranches
        .filter((t) => t.type === 'initial')
        .reduce((sum, t) => sum + (Number(t.amount) || 0), 0)
    : Math.max(0, invested - capitalAddedFromProposals);

  const capitalAdded = hasTrancheBreakdown
    ? positiveInflowTranches
        .filter((t) => t.type === 'manual' || t.type === 'capital_addition')
        .reduce((sum, t) => sum + (Number(t.amount) || 0), 0)
    : capitalAddedFromProposals;

  const totalRepaid = repayments.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
  const balance = Math.max(0, invested - totalRepaid);

  const formatMovementDate = (val) => {
    if (!val) return '—';
    const iso = typeof val === 'string' ? val.slice(0, 10) : (val?.toISOString?.()?.slice(0, 10) || val?.toDate?.()?.toISOString?.()?.slice(0, 10));
    if (!iso) return '—';
    const dateLocale = language === 'fr' ? 'fr-CA' : 'en-CA';
    try {
      return new Date(iso + 'T12:00:00').toLocaleDateString(dateLocale, { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return iso;
    }
  };

  const fundMovements = useMemo(() => {
    const withdrawals = repayments.map((r) => ({
      id: r.id,
      date: r.repaidAt,
      amount: r.amount,
      type: 'withdrawal',
      movementKind: null,
    }));
    if (positiveInflowTranches.length > 0) {
      const fromTranches = positiveInflowTranches.map((t) => ({
        id: `tr-${t.id}`,
        date: t.startDate,
        amount: t.amount,
        type: 'capital',
        movementKind: t.type === 'initial' ? 'initial' : 'addition',
      }));
      return [...withdrawals, ...fromTranches].sort((a, b) => {
        const da = typeof a.date === 'string' ? a.date : (a.date?.toDate?.()?.toISOString?.() ?? a.date?.toISOString?.() ?? '');
        const db = typeof b.date === 'string' ? b.date : (b.date?.toDate?.()?.toISOString?.() ?? b.date?.toISOString?.() ?? '');
        return db.localeCompare(da);
      });
    }
    const fromProposals = capitalProposals
      .filter((p) => p.fundsReceivedAt || p.fundsReceivedAmount != null)
      .map((p) => ({
        id: `cap-${p.id}`,
        date: p.fundsReceivedAt || (p.respondedAt && typeof p.respondedAt === 'string' ? p.respondedAt.slice(0, 10) : ''),
        amount: p.fundsReceivedAmount ?? p.proposedAmount,
        type: 'capital',
        movementKind: 'addition',
      }));
    return [...withdrawals, ...fromProposals].sort((a, b) => {
      const da = typeof a.date === 'string' ? a.date : (a.date?.toDate?.()?.toISOString?.() ?? a.date?.toISOString?.() ?? '');
      const db = typeof b.date === 'string' ? b.date : (b.date?.toDate?.()?.toISOString?.() ?? b.date?.toISOString?.() ?? '');
      return db.localeCompare(da);
    });
  }, [repayments, positiveInflowTranches, capitalProposals]);

  const handleTransactionSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const dateStr = transactionDate?.trim().slice(0, 10);
    if (!dateStr) {
      setError(t('fundInvestors.transaction.errorDate'));
      return;
    }
    if (new Date(dateStr) > new Date(today)) {
      setError(t('fundInvestors.transaction.noFutureDate'));
      return;
    }
    const amountNum = parsePaidAmountDisplay(String(transactionAmount));
    if (Number.isNaN(amountNum) || amountNum <= 0) {
      setError(t('fundInvestors.repayments.errorAmount'));
      return;
    }
    setSubmitting(true);
    try {
      if (transactionForm.type === 'withdrawal') {
        await onCompleteWithdrawalWithTransaction(transactionForm.request.id, item.id, {
          transactionDate: dateStr,
          amount: amountNum,
          investingEntityType: transactionForm.request.investingEntityType,
          investingCompanyId: transactionForm.request.investingCompanyId,
          investingDisplayName: transactionForm.request.investingDisplayName,
        });
      } else {
        await onConfirmCapitalReceipt(transactionForm.proposal.id, { transactionDate: dateStr, amount: amountNum });
      }
      setTransactionForm(null);
    } catch (err) {
      setError(err?.message || t('fundInvestors.invite.errorGeneric'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenInvestorCheque = async () => {
    const path = item?.chequeSpecimenFileRef?.storagePath;
    if (!path) return;
    setChequeBannerError('');
    try {
      const url = await getFundInvestorFileDownloadUrl(path);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      setChequeBannerError(t('fundInvestors.chequeSpecimen.uploadError'));
    }
  };

  const handleAckChequeSpecimen = async () => {
    if (!item?.id) return;
    setChequeAckSubmitting(true);
    setChequeBannerError('');
    try {
      await acknowledgeFundInvestorChequeSpecimen(item.id);
    } catch (err) {
      setChequeBannerError(err?.message || t('fundInvestors.invite.errorGeneric'));
    } finally {
      setChequeAckSubmitting(false);
    }
  };

  const handleOpenQualificationDoc = async (storagePath) => {
    if (!storagePath) return;
    setQualificationDocsActionError('');
    try {
      const url = await getFundInvestorFileDownloadUrl(storagePath);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setQualificationDocsActionError(err?.message || t('fundInvestors.qualificationDocs.uploadError'));
    }
  };

  const handleRequestQualificationDocs = async () => {
    if (!item?.id) return;
    setQualificationDocsActionError('');

    if (!isFundInvestorAttestationSatisfied(item)) {
      setQualificationDocsActionError(t('fundInvestors.qualified.completeFirst'));
      return;
    }

    const category =
      item?.qualifiedInvestorCategory ||
      item?.qualificationDocsCategory ||
      (item?.status === 'funds_received' ? 'accredited' : null);
    if (!category) {
      setQualificationDocsActionError(t('fundInvestors.qualificationDocs.noCategoryError'));
      return;
    }

    setQualificationDocsRequestLoading(true);
    try {
      await requestQualificationDocs(item.id, category);
    } catch (err) {
      setQualificationDocsActionError(err?.message || t('fundInvestors.qualificationDocs.requestError'));
    } finally {
      setQualificationDocsRequestLoading(false);
    }
  };

  const handleReviewQualificationDocs = async () => {
    if (!item?.id) return;
    setQualificationDocsActionError('');
    setQualificationDocsRequestLoading(true);
    try {
      await reviewQualificationDocs(item.id);
    } catch (err) {
      setQualificationDocsActionError(err?.message || t('fundInvestors.qualificationDocs.reviewError'));
    } finally {
      setQualificationDocsRequestLoading(false);
    }
  };

  const handleOpenPlacementContract = async () => {
    const path = item?.contractFileRef?.storagePath;
    if (!path) return;
    setChequeBannerError('');
    try {
      const url = await getFundInvestorFileDownloadUrl(path);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      setChequeBannerError(t('fundInvestors.chequeSpecimen.uploadError'));
    }
  };

  const handleOpenInvestorSignedContract = async () => {
    const path = item?.investorSignedContractFileRef?.storagePath;
    if (!path) return;
    setChequeBannerError('');
    try {
      const url = await getFundInvestorFileDownloadUrl(path);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      setChequeBannerError(t('fundInvestors.chequeSpecimen.uploadError'));
    }
  };

  const handleOpenFullyExecutedContract = async () => {
    const path = item?.fullyExecutedContractFileRef?.storagePath;
    if (!path) return;
    setChequeBannerError('');
    try {
      const url = await getFundInvestorFileDownloadUrl(path);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      setChequeBannerError(t('fundInvestors.chequeSpecimen.uploadError'));
    }
  };

  const handleFullyExecutedContractFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !item?.id) return;
    const maxBytes = 20 * 1024 * 1024;
    setFullyExecutedError('');
    setFullyExecutedSuccess('');
    if (file.size > maxBytes) {
      setFullyExecutedError(t('fundInvestors.contractSigned.fileTooLarge'));
      return;
    }
    if (file.type !== 'application/pdf' && !String(file.name || '').toLowerCase().endsWith('.pdf')) {
      setFullyExecutedError(t('fundInvestors.contractSigned.pdfOnly'));
      return;
    }
    setFullyExecutedUploading(true);
    try {
      await setFundInvestorFullyExecutedContract(item.id, file);
      setFullyExecutedSuccess(t('fundInvestors.contractSigned.lenderUploadFullyExecutedSuccess'));
      setTimeout(() => setFullyExecutedSuccess(''), 6000);
    } catch (err) {
      setFullyExecutedError(err?.message || t('fundInvestors.contractSigned.uploadError'));
    } finally {
      setFullyExecutedUploading(false);
    }
  };

  const statusMeta = item?.status && STATUS_LABELS[item.status];

  useRegisterTopEscapeClose(() => {
    if (transactionForm) {
      setTransactionForm(null);
      setError('');
      return;
    }
    if (
      submitting
      || chequeAckSubmitting
      || qualificationDocsRequestLoading
      || fullyExecutedUploading
    ) {
      return;
    }
    onClose();
  });

  return (
           <div
             className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-6"
             onClick={onClose}
             role="presentation"
           >
             <div
               className="bg-white rounded-xl shadow-xl border border-gray-200 max-w-3xl w-full min-w-0 max-h-[min(92vh,880px)] flex flex-col overflow-hidden"
               onClick={(e) => e.stopPropagation()}
               role="dialog"
               aria-labelledby="manage-capital-investor-title"
             >
               <div className="flex items-start justify-between gap-3 p-4 sm:p-5 border-b border-gray-200 shrink-0 bg-white">
                 <div className="min-w-0">
                   <h3 id="manage-capital-investor-title" className="text-lg sm:text-xl font-semibold text-gray-900 leading-tight">
                     {t('fundInvestors.manageCapitalInvestor')}
                   </h3>
                   <p className="text-sm text-indigo-600 font-medium mt-0.5 truncate">{item.displayName || item.email}</p>
                   {item.displayName && item.email && (
                     <p className="text-xs text-gray-500 truncate mt-0.5">{item.email}</p>
                   )}
                   <div className="mt-2 flex flex-wrap items-center gap-2">
                     {statusMeta && (
                       <span className={`inline-flex px-2 py-0.5 rounded-md text-xs font-medium ${statusMeta.color}`}>
                         {t(statusMeta.key)}
                       </span>
                     )}
                     {item.interestStartDate && (
                       <span className="text-xs text-gray-500">
                         {t('fundInvestors.table.startDate')}: {formatDate(item.interestStartDate)}
                       </span>
                     )}
                   </div>
                 </div>
                 <button
                   type="button"
                   onClick={onClose}
                   className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 shrink-0"
                 >
                   <span className="sr-only">{t('cancel')}</span>
                   <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                   </svg>
                 </button>
               </div>

               {/* KPI strip */}
               <div className="px-4 sm:px-5 py-3 border-b border-gray-100 bg-gradient-to-b from-slate-50 to-white shrink-0">
                 <div className="grid grid-cols-1 lg:grid-cols-4 gap-2 sm:gap-3">
                   <div className="rounded-xl border border-gray-200/80 bg-white p-3 shadow-sm">
                     <span className="text-[11px] sm:text-xs font-medium text-gray-500 uppercase tracking-wide block mb-0.5">
                       {t('fundInvestors.repayments.investedInitially')}
                     </span>
                     <span className="text-base sm:text-lg font-semibold text-gray-900 tabular-nums">{formatAmount(initialInvested)}</span>
                   </div>
                   <div className="rounded-xl border border-emerald-100 bg-emerald-50/50 p-3 shadow-sm">
                     <span className="text-[11px] sm:text-xs font-medium text-emerald-800/80 uppercase tracking-wide block mb-0.5">
                       {t('fundInvestors.repayments.capitalAdded')}
                     </span>
                     <span className="text-base sm:text-lg font-semibold text-emerald-900 tabular-nums">{formatAmount(capitalAdded)}</span>
                   </div>
                   <div className="rounded-xl border border-amber-100 bg-amber-50/50 p-3 shadow-sm">
                     <span className="text-[11px] sm:text-xs font-medium text-amber-900/80 uppercase tracking-wide block mb-0.5">
                       {t('fundInvestors.repayments.totalRepaid')}
                     </span>
                     <span className="text-base sm:text-lg font-semibold text-amber-950 tabular-nums">{formatAmount(totalRepaid)}</span>
                   </div>
                   <div className="rounded-xl border border-sky-200 bg-sky-50/60 p-3 shadow-sm col-span-1">
                     <span className="text-[11px] sm:text-xs font-medium text-sky-900/80 uppercase tracking-wide block mb-0.5">
                       {t('fundInvestors.repayments.balance')}
                     </span>
                     <span className="text-base sm:text-lg font-semibold text-sky-950 tabular-nums">{formatAmount(balance)}</span>
                   </div>
                 </div>
               </div>

               <div className="flex shrink-0 border-b border-gray-200 px-2 sm:px-3 gap-0.5 bg-gray-50/60" role="tablist">
                 <button
                   type="button"
                   role="tab"
                   aria-selected={lenderModalTab === 'overview'}
                   onClick={() => setLenderModalTab('overview')}
                   className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 px-2 sm:px-3 text-xs sm:text-sm font-medium rounded-t-lg border-b-2 -mb-px transition-colors min-w-0 ${
                     lenderModalTab === 'overview'
                       ? 'border-indigo-600 text-indigo-700 bg-white shadow-sm'
                       : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-white/70'
                   }`}
                 >
                   <LayoutGrid className="w-4 h-4 shrink-0 opacity-80" />
                   <span className="truncate">{t('fundInvestors.lenderModalTabOverview')}</span>
                 </button>
                 <button
                   type="button"
                   role="tab"
                   aria-selected={lenderModalTab === 'documents'}
                   onClick={() => setLenderModalTab('documents')}
                   className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 px-2 sm:px-3 text-xs sm:text-sm font-medium rounded-t-lg border-b-2 -mb-px transition-colors min-w-0 ${
                     lenderModalTab === 'documents'
                       ? 'border-indigo-600 text-indigo-700 bg-white shadow-sm'
                       : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-white/70'
                   }`}
                 >
                   <Banknote className="w-4 h-4 shrink-0 opacity-80" />
                   <span className="truncate">{t('fundInvestors.lenderModalTabDocuments')}</span>
                 </button>
                 <button
                   type="button"
                   role="tab"
                   aria-selected={lenderModalTab === 'requests'}
                   onClick={() => setLenderModalTab('requests')}
                   className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 px-2 sm:px-3 text-xs sm:text-sm font-medium rounded-t-lg border-b-2 -mb-px transition-colors min-w-0 ${
                     lenderModalTab === 'requests'
                       ? 'border-indigo-600 text-indigo-700 bg-white shadow-sm'
                       : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-white/70'
                   }`}
                 >
                   <ClipboardList className="w-4 h-4 shrink-0 opacity-80" />
                   <span className="truncate">{t('fundInvestors.lenderModalTabRequests')}</span>
                 </button>
               </div>

               <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-4 sm:p-5 space-y-5">
                 {lenderModalTab === 'overview' && (
                   <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                     {item.legacyCapitalImport && (
                       <p className="text-sm text-amber-900 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mb-3">
                         {t('fundInvestors.legacyImport.modalNote')}
                       </p>
                     )}
                     <h4 className="text-sm font-semibold text-gray-900 mb-3">{t('fundInvestors.fundMovements.title')}</h4>
                     {fundMovements.length === 0 ? (
                       <p className="text-sm text-gray-500">{t('fundInvestors.fundMovements.empty')}</p>
                     ) : (
                       <div className="border border-gray-200 rounded-lg overflow-x-auto">
                         <table className="min-w-full text-sm">
                           <thead className="bg-gray-50">
                             <tr>
                               <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">{t('fundInvestors.fundMovements.date')}</th>
                               <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">{t('fundInvestors.fundMovements.amount')}</th>
                               <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">{t('fundInvestors.fundMovements.type')}</th>
                             </tr>
                           </thead>
                           <tbody className="divide-y divide-gray-200">
                             {fundMovements.map((m) => (
                               <tr key={m.id} className="hover:bg-gray-50/80">
                                 <td className="px-3 py-2 whitespace-nowrap">{formatMovementDate(m.date)}</td>
                                 <td className="px-3 py-2 text-right tabular-nums">{formatAmount(m.amount)}</td>
                                 <td className="px-3 py-2">
                                   {m.type === 'withdrawal'
                                     ? t('fundInvestors.fundMovements.typeWithdrawal')
                                     : m.movementKind === 'initial'
                                       ? t('fundInvestors.fundMovements.typeInitialSubscription')
                                       : t('fundInvestors.fundMovements.typeCapital')}
                                 </td>
                               </tr>
                             ))}
                           </tbody>
                         </table>
                       </div>
                     )}
                   </section>
                 )}

                 {lenderModalTab === 'documents' && (
                   <div className="space-y-5">
                     {chequeBannerError && (
                       <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                         {chequeBannerError}
                       </p>
                     )}
                     <section className="rounded-xl border border-indigo-100 bg-indigo-50/35 p-4 shadow-sm space-y-2">
                       <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                         <User className="w-4 h-4 text-indigo-700 shrink-0" />
                         {t('fundInvestors.lenderModalInvestorInfo')}
                       </h4>
                       <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
                         <div>
                           <dt className="text-xs font-medium text-gray-500">{t('fundInvestors.table.email')}</dt>
                           <dd className="text-gray-900 break-all">{item.email || '—'}</dd>
                         </div>
                         <div>
                           <dt className="text-xs font-medium text-gray-500">{t('fundInvestors.invite.displayName')}</dt>
                           <dd className="text-gray-900">{item.displayName || '—'}</dd>
                         </div>
                         <div className="sm:col-span-2">
                           <dt className="text-xs font-medium text-gray-500">{t('fundInvestors.investingHolder')}</dt>
                           <dd className="text-gray-900">
                             {item.investingDisplayName ||
                               (item.investingEntityType === 'company'
                                 ? item.investingCompanyId || '—'
                                 : t('fundInvestors.investingPersonal'))}
                           </dd>
                         </div>
                         <div className="sm:col-span-2">
                           <dt className="text-xs font-medium text-gray-500">{t('fundInvestors.qualified.lenderFieldLabel')}</dt>
                           <dd className="text-gray-900 text-sm">
                             {!isFundInvestorAttestationSatisfied(item) ? (
                               <span className="text-amber-800 font-medium">{t('fundInvestors.qualified.lenderPending')}</span>
                             ) : item.qualifiedInvestorAttestedAt != null ? (
                               <span>
                                 {t('fundInvestors.qualified.lenderAttested')}{' '}
                                 {formatFromFirestoreDate(item.qualifiedInvestorAttestedAt)}
                                 {item.qualifiedInvestorCategory && (
                                   <span className="text-gray-600">
                                     {' '}—{' '}
                                     {item.qualifiedInvestorCategory === 'accredited'
                                       ? t('fundInvestors.qualified.lenderCategoryAccredited')
                                       : item.qualifiedInvestorCategory === 'eligible'
                                         ? t('fundInvestors.qualified.lenderCategoryEligible')
                                         : t('fundInvestors.qualified.lenderCategoryInstitution')}
                                   </span>
                                 )}
                               </span>
                             ) : (
                               <span className="text-emerald-900 font-medium">
                                 {t('fundInvestors.qualified.lenderSatisfiedViaFundsReceived')}
                               </span>
                             )}
                           </dd>
                         </div>
                       </dl>
                     </section>

                    <section className="rounded-xl border border-amber-100 bg-amber-50/40 p-4 shadow-sm space-y-3">
                      <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                        <ClipboardList className="w-4 h-4 text-amber-700 shrink-0" />
                        {t('fundInvestors.qualificationDocs.title')}
                      </h4>
                      <p className="text-xs text-gray-600 leading-relaxed">{t('fundInvestors.qualificationDocs.intro')}</p>
                      <div className="flex flex-wrap items-center gap-2">
                        <a
                          href="https://lautorite.qc.ca/fileadmin/lautorite/reglementation/valeurs-mobilieres/45-106/2025-09-19/2025sept19-45-106-vofficielle-fr.pdf"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-amber-800 font-medium hover:underline"
                        >
                          NI 45-106 (FR)
                        </a>
                        <a
                          href="https://lautorite.qc.ca/fileadmin/lautorite/reglementation/valeurs-mobilieres/45-106/2025-09-19/2025sept19-45-106-vofficielle-en.pdf"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-amber-800 font-medium hover:underline"
                        >
                          NI 45-106 (EN)
                        </a>
                      </div>

                      {(() => {
                        const requestStatus = item?.qualificationDocsRequestStatus || 'not_requested';
                        const category = item?.qualificationDocsCategory || item?.qualifiedInvestorCategory || 'accredited';
                        const uploads = item?.qualificationDocsUploads || {};
                        const requiredDocs = getQualificationDocsTemplate(category);

                        if (requestStatus === 'not_requested') {
                          return (
                            <div className="space-y-2">
                              <p className="text-sm text-gray-700">{t('fundInvestors.qualificationDocs.status_not_requested')}</p>
                              {isFundInvestorAttestationSatisfied(item) ? (
                                <button
                                  type="button"
                                  onClick={handleRequestQualificationDocs}
                                  disabled={qualificationDocsRequestLoading}
                                  className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
                                >
                                  {qualificationDocsRequestLoading ? '…' : t('fundInvestors.qualificationDocs.requestButton')}
                                </button>
                              ) : (
                                <p className="text-xs text-gray-500">{t('fundInvestors.qualificationDocs.waitForAttestation')}</p>
                              )}
                            </div>
                          );
                        }

                        const statusLine =
                          requestStatus === 'requested'
                            ? t('fundInvestors.qualificationDocs.status_requested')
                            : requestStatus === 'submitted'
                              ? t('fundInvestors.qualificationDocs.status_submitted')
                              : t('fundInvestors.qualificationDocs.status_reviewed');

                        return (
                          <div className="space-y-3">
                            <div className="text-sm text-gray-700 font-medium">{statusLine}</div>
                            <div className="space-y-2">
                              {requiredDocs.map((docItem) => {
                                const uploaded = uploads?.[docItem.key];
                                return (
                                  <div key={docItem.key} className="rounded-lg border border-gray-200 bg-white p-3 space-y-2">
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                      <span className="text-sm font-medium text-gray-900">{t(docItem.labelKey)}</span>
                                      {uploaded?.storagePath ? (
                                        <button
                                          type="button"
                                          onClick={() => handleOpenQualificationDoc(uploaded.storagePath)}
                                          className="text-sm text-amber-800 font-medium hover:underline"
                                        >
                                          {t('fundInvestors.qualificationDocs.open')}
                                        </button>
                                      ) : (
                                        <span className="text-xs text-gray-500">{t('fundInvestors.qualificationDocs.notUploaded')}</span>
                                      )}
                                    </div>
                                    {uploaded?.name && <div className="text-xs text-gray-600">{uploaded.name}</div>}
                                  </div>
                                );
                              })}
                            </div>

                            {requestStatus === 'submitted' && (
                              <button
                                type="button"
                                onClick={handleReviewQualificationDocs}
                                disabled={qualificationDocsRequestLoading}
                                className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
                              >
                                {qualificationDocsRequestLoading ? '…' : t('fundInvestors.qualificationDocs.markReviewed')}
                              </button>
                            )}
                          </div>
                        );
                      })()}

                      {qualificationDocsActionError && (
                        <p className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                          {qualificationDocsActionError}
                        </p>
                      )}
                    </section>

                     {item.contractFileRef?.storagePath && (
                       <section className="rounded-xl border border-slate-200 bg-slate-50/50 p-4 shadow-sm space-y-3">
                         <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2 flex-wrap">
                           <FileText className="w-4 h-4 text-slate-700 shrink-0" />
                           {t('fundInvestors.lenderModalContract')}
                         </h4>
                         <p className="text-xs text-gray-600 leading-relaxed">
                           {t('fundInvestors.contractSigned.lenderWorkflowHint')}
                         </p>
                         {item.investorSignedContractPendingLenderNotice && (
                           <p className="text-xs font-medium text-violet-900 bg-violet-50 border border-violet-200 rounded-lg px-3 py-2">
                             {t('fundInvestors.contractSigned.pendingInvestorSignedNotice')}
                           </p>
                         )}
                         <div>
                           <p className="text-xs font-medium text-gray-700 mb-1">
                             {t('fundInvestors.contractSigned.templateLabel')}
                           </p>
                           <p className="text-xs text-gray-600 mb-1">
                             <span className="font-medium text-gray-700">{item.contractFileRef.name || 'PDF'}</span>
                           </p>
                           <button
                             type="button"
                             onClick={handleOpenPlacementContract}
                             className="text-sm text-indigo-700 font-medium hover:underline"
                           >
                             {t('privateLenderOffer.downloadContract')}
                           </button>
                         </div>
                         <div className="border-t border-gray-200 pt-3 space-y-2">
                           <p className="text-xs font-medium text-gray-700">
                             {t('fundInvestors.contractSigned.investorSignedBlockTitle')}
                           </p>
                           {item.investorSignedContractFileRef?.storagePath ? (
                             <div className="space-y-1">
                               <p className="text-xs text-gray-600 break-all">
                                 <span className="font-medium">{item.investorSignedContractFileRef.name || 'PDF'}</span>
                               </p>
                               {item.investorSignedContractUploadedAt && (
                                 <p className="text-xs text-gray-500">
                                   {t('fundInvestors.contractSigned.investorSignedUploadedAt')}:{' '}
                                   {formatFromFirestoreDate(item.investorSignedContractUploadedAt)}
                                 </p>
                               )}
                               <button
                                 type="button"
                                 onClick={handleOpenInvestorSignedContract}
                                 className="text-sm text-indigo-700 font-medium hover:underline"
                               >
                                 {t('fundInvestors.contractSigned.openInvestorSigned')}
                               </button>
                             </div>
                           ) : (
                             <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                               {t('fundInvestors.contractSigned.noInvestorSignedYet')}
                             </p>
                           )}
                         </div>
                         <div className="border-t border-gray-200 pt-3 space-y-2">
                           <p className="text-xs font-medium text-gray-700">
                             {t('fundInvestors.contractSigned.fullyExecutedBlockTitle')}
                           </p>
                           <p className="text-xs text-gray-500">{t('fundInvestors.contractSigned.fullyExecutedHint')}</p>
                           {item.fullyExecutedContractFileRef?.storagePath ? (
                             <div className="space-y-2">
                               <p className="text-xs text-gray-600 break-all">
                                 <span className="font-medium">{item.fullyExecutedContractFileRef.name || 'PDF'}</span>
                               </p>
                               {item.fullyExecutedContractUploadedAt && (
                                 <p className="text-xs text-gray-500">
                                   {t('fundInvestors.contractSigned.fullyExecutedUploadedAt')}:{' '}
                                   {formatFromFirestoreDate(item.fullyExecutedContractUploadedAt)}
                                 </p>
                               )}
                               <button
                                 type="button"
                                 onClick={handleOpenFullyExecutedContract}
                                 className="text-sm text-indigo-700 font-medium hover:underline"
                               >
                                 {t('fundInvestors.contractSigned.openFullyExecuted')}
                               </button>
                             </div>
                           ) : null}
                           <label className="block text-xs font-medium text-gray-700">
                             {item.fullyExecutedContractFileRef?.storagePath
                               ? t('fundInvestors.contractSigned.replaceFullyExecutedUpload')
                               : t('fundInvestors.contractSigned.uploadFullyExecutedLabel')}
                           </label>
                           <input
                             type="file"
                             accept="application/pdf,.pdf"
                             disabled={fullyExecutedUploading}
                             onChange={handleFullyExecutedContractFile}
                             className="w-full text-sm text-gray-600 file:mr-2 file:py-2 file:px-3 file:rounded-lg file:border file:border-gray-300 file:bg-white file:text-sm disabled:opacity-50"
                           />
                           {fullyExecutedUploading && (
                             <p className="text-xs text-gray-500">{t('fundInvestors.contractSigned.uploading')}</p>
                           )}
                           {fullyExecutedError && (
                             <p className="text-xs text-red-600">{fullyExecutedError}</p>
                           )}
                           {fullyExecutedSuccess && (
                             <p className="text-xs text-green-700 font-medium">{fullyExecutedSuccess}</p>
                           )}
                         </div>
                       </section>
                     )}

                     <section
                       className={`rounded-xl border p-4 shadow-sm space-y-2 ${
                         item.chequeSpecimenPendingLenderNotice
                           ? 'border-amber-300 bg-amber-50/70'
                           : 'border-emerald-100 bg-emerald-50/40'
                       }`}
                     >
                       <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                         <Banknote className="w-4 h-4 text-emerald-700 shrink-0" />
                         {t('fundInvestors.chequeSpecimen.lenderSection')}
                       </h4>
                       {item.chequeSpecimenPendingLenderNotice && (
                         <p className="text-xs font-medium text-amber-900">{t('fundInvestors.chequeSpecimen.pendingBadge')}</p>
                       )}
                       {item.chequeSpecimenFileRef?.storagePath ? (
                         <div className="text-sm text-gray-700 space-y-2">
                           <p>
                             <span className="text-gray-500">{t('fundInvestors.chequeSpecimen.currentFile')}: </span>
                             <span className="font-medium">{item.chequeSpecimenFileRef.name || '—'}</span>
                           </p>
                           {item.chequeSpecimenUpdatedAt && (
                             <p className="text-xs text-gray-500">
                               {t('fundInvestors.chequeSpecimen.updatedAt')}: {formatFromFirestoreDate(item.chequeSpecimenUpdatedAt)}
                             </p>
                           )}
                           <div className="flex flex-wrap gap-2">
                             <button
                               type="button"
                               onClick={handleOpenInvestorCheque}
                               className="px-3 py-1.5 text-sm border border-emerald-600 text-emerald-800 rounded-lg hover:bg-emerald-50"
                             >
                               {t('fundInvestors.chequeSpecimen.open')}
                             </button>
                             {item.chequeSpecimenPendingLenderNotice && (
                               <button
                                 type="button"
                                 onClick={handleAckChequeSpecimen}
                                 disabled={chequeAckSubmitting}
                                 className="px-3 py-1.5 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
                               >
                                 {chequeAckSubmitting
                                   ? t('fundInvestors.chequeSpecimen.acknowledging')
                                   : t('fundInvestors.chequeSpecimen.acknowledge')}
                               </button>
                             )}
                           </div>
                         </div>
                       ) : (
                         <p className="text-sm text-gray-600">{t('fundInvestors.chequeSpecimen.none')}</p>
                       )}
                     </section>
                   </div>
                 )}

                 {lenderModalTab === 'requests' && (
                   <div className="space-y-5">
                     <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                       <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                         <ArrowDownToLine className="w-4 h-4 text-gray-600 shrink-0" />
                         {t('withdrawalRequests.title')}
                       </h4>
                       {withdrawalRequests.length === 0 ? (
                         <p className="text-sm text-gray-500">{t('withdrawalRequests.empty')}</p>
                       ) : (
                         <ul className="divide-y divide-gray-100 text-sm">
                           {withdrawalRequests.map((req) => (
                             <li key={req.id} className="py-3 first:pt-0">
                               <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-1">
                                 <span className="text-gray-900">
                                   {formatFromFirestoreDate(req.requestedAt)} —{' '}
                                   {req.amountRequested != null ? formatAmount(req.amountRequested) : '—'}
                                 </span>
                                 <span className="flex items-center gap-2 shrink-0">
                                   {req.status === 'completed' && req.repaidAt ? (
                                     <span className="text-xs text-green-700 font-medium">
                                       {t('withdrawalRequests.repaidOn')} {formatMovementDate(req.repaidAt)}
                                     </span>
                                   ) : (
                                     <>
                                       <span
                                         className={`inline-flex px-2 py-0.5 rounded-md text-xs font-medium ${
                                           req.status === 'completed'
                                             ? 'bg-green-100 text-green-800'
                                             : req.status === 'cancelled'
                                               ? 'bg-gray-100 text-gray-600'
                                               : 'bg-amber-100 text-amber-800'
                                         }`}
                                       >
                                         {t(
                                           `withdrawalRequests.status${
                                             req.status === 'completed'
                                               ? 'Completed'
                                               : req.status === 'cancelled'
                                                 ? 'Cancelled'
                                                 : 'Pending'
                                           }`,
                                         )}
                                       </span>
                                       {req.status === 'pending' && (
                                         <button
                                           type="button"
                                           onClick={() => setTransactionForm({ type: 'withdrawal', request: req })}
                                           disabled={updatingWithdrawalId === req.id}
                                           className="text-xs text-green-700 font-medium hover:underline disabled:opacity-50"
                                         >
                                           {t('withdrawalRequests.markCompleted')}
                                         </button>
                                       )}
                                     </>
                                   )}
                                 </span>
                               </div>
                               <p className="text-xs text-gray-500 mt-1">
                                 {t('fundInvestors.investingHolder')}:{' '}
                                 {req.investingDisplayName ||
                                   (req.investingEntityType === 'company'
                                     ? req.investingCompanyId || '—'
                                     : t('fundInvestors.investingPersonal'))}
                               </p>
                               {req.comment && (
                                 <p className="text-xs text-gray-600 mt-1 italic">
                                   {t('withdrawalRequests.investorComment')}: {req.comment}
                                 </p>
                               )}
                             </li>
                           ))}
                         </ul>
                       )}
                     </section>

                     <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                       <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                         <PlusCircle className="w-4 h-4 text-gray-600 shrink-0" />
                         {t('capitalAddition.title')}
                       </h4>
                       {capitalProposals.length === 0 ? (
                         <p className="text-sm text-gray-500">{t('capitalAddition.empty')}</p>
                       ) : (
                         <ul className="divide-y divide-gray-100 text-sm">
                           {capitalProposals.map((p) => (
                             <li key={p.id} className="py-3 first:pt-0 flex flex-col gap-1">
                               <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-1">
                                 <span className="text-gray-900">
                                   {formatAmount(p.proposedAmount)} — {p.availableFromDate ? formatDate(p.availableFromDate) : '—'}
                                 </span>
                                 <span className="flex flex-wrap items-center gap-2 shrink-0">
                                   {p.fundsReceivedAt ? (
                                     <span className="text-xs text-green-700 font-medium">
                                       {t('fundInvestors.transaction.receivedOn')} {formatMovementDate(p.fundsReceivedAt)}
                                     </span>
                                   ) : (
                                     <>
                                       <span
                                         className={`inline-flex px-2 py-0.5 rounded-md text-xs font-medium ${
                                           p.status === 'accepted'
                                             ? 'bg-green-100 text-green-800'
                                             : p.status === 'rejected'
                                               ? 'bg-red-100 text-red-800'
                                               : 'bg-amber-100 text-amber-800'
                                         }`}
                                       >
                                         {t(
                                           `capitalAddition.status${
                                             p.status === 'accepted' ? 'Accepted' : p.status === 'rejected' ? 'Rejected' : 'Pending'
                                           }`,
                                         )}
                                       </span>
                                       {p.status === 'pending' && (
                                         <span className="inline-flex gap-2">
                                           <button
                                             type="button"
                                             onClick={() => onRespondProposal(p.id, 'accepted')}
                                             disabled={respondingProposalId === p.id}
                                             className="text-xs text-green-700 font-medium hover:underline disabled:opacity-50"
                                           >
                                             {t('capitalAddition.accept')}
                                           </button>
                                           <button
                                             type="button"
                                             onClick={() => onRespondProposal(p.id, 'rejected')}
                                             disabled={respondingProposalId === p.id}
                                             className="text-xs text-red-600 font-medium hover:underline disabled:opacity-50"
                                           >
                                             {t('capitalAddition.reject')}
                                           </button>
                                         </span>
                                       )}
                                       {p.status === 'accepted' && (
                                         <button
                                           type="button"
                                           onClick={() => setTransactionForm({ type: 'capital', proposal: p })}
                                           disabled={respondingProposalId === p.id}
                                           className="text-xs text-green-700 font-medium hover:underline disabled:opacity-50"
                                         >
                                           {t('fundInvestors.transaction.confirmReceipt')}
                                         </button>
                                       )}
                                     </>
                                   )}
                                 </span>
                               </div>
                               <p className="text-xs text-gray-500">
                                 {t('fundInvestors.investingHolder')}:{' '}
                                 {p.investingDisplayName ||
                                   (p.investingEntityType === 'company'
                                     ? p.investingCompanyId || '—'
                                     : t('fundInvestors.investingPersonal'))}
                               </p>
                             </li>
                           ))}
                         </ul>
                       )}
                     </section>

                     {transactionForm && (
                       <div className="rounded-xl border border-indigo-200 bg-indigo-50/50 p-4 shadow-sm">
                         <h4 className="text-sm font-semibold text-gray-900 mb-3">{t('fundInvestors.transaction.title')}</h4>
                         <form onSubmit={handleTransactionSubmit} className="space-y-3">
                           {error && <p className="text-sm text-red-600">{error}</p>}
                           <div className="grid grid-cols-1 gap-3">
                             <div>
                               <label className="block text-xs font-medium text-gray-600 mb-0.5">
                                 {t('fundInvestors.transaction.date')}
                               </label>
                               <input
                                 type="date"
                                 value={transactionDate}
                                 onChange={(e) => setTransactionDate(e.target.value)}
                                 max={today}
                                 required
                                 className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                               />
                             </div>
                             <div>
                               <label className="block text-xs font-medium text-gray-600 mb-0.5">
                                 {t('fundInvestors.transaction.amount')}
                               </label>
                               <input
                                 type="text"
                                 value={transactionAmount}
                                 onChange={(e) => setTransactionAmount(e.target.value)}
                                 onBlur={(e) => {
                                   const n = parsePaidAmountDisplay(e.target.value);
                                   if (!Number.isNaN(n) && n >= 0) setTransactionAmount(formatPaidAmountDisplay(n));
                                 }}
                                 className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                                 placeholder="0"
                                 required
                               />
                             </div>
                           </div>
                           <div className="flex flex-wrap gap-2 pt-1">
                             <button
                               type="button"
                               onClick={() => {
                                 setTransactionForm(null);
                                 setError('');
                               }}
                               className="px-4 py-2 text-sm text-gray-700 rounded-lg border border-gray-300 bg-white hover:bg-gray-50"
                             >
                               {t('cancel')}
                             </button>
                             <button
                               type="submit"
                               disabled={submitting}
                               className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium"
                             >
                               {submitting ? t('fundInvestors.transaction.submitting') : t('fundInvestors.transaction.submit')}
                             </button>
                           </div>
                         </form>
                       </div>
                     )}
                   </div>
                 )}
               </div>
             </div>
           </div>
         );
}
